// Admin configuration types for margin thresholds and rules

export interface AssetTypeAddOns {
  // Equity categories
  largeCap: number; // +5% (>$10B market cap)
  midCap: number;   // +10% ($2-10B market cap)
  smallCap: number; // +15% (<$2B market cap)
  volatile: number; // +20% (>40% volatility)
  pennyStocks: number; // 100% (banned - cash only)
  
  // Other asset types
  etfs: number;        // +5%
  bonds: number;       // +5%
  mutualFunds: number; // +5%
  options: number;     // +5%
  other: number;       // +5%
}

export interface MarketCapDefinitions {
  largeCap: number;  // >$10B (default)
  midCap: number;    // $2B (lower bound for mid cap)
  smallCap: number;  // Below this is small cap
}

export interface VolatilityThresholds {
  volatileStock: number; // 40% (30-day annualized volatility)
  pennyStock: number;    // $5 (price threshold)
}

export interface ConcentrationAddOns {
  singlePosition30Percent: number; // +10% for single position >30% of portfolio
  singlePosition50Percent: number; // +20% for single position >50% of portfolio
  top3Positions70Percent: number;  // +10% for top 3 positions >70% of portfolio (applied to positions >20%)
}

export interface WarningThresholds {
  greenZone: number;      // >30% surplus cushion
  amberAlertZone: number; // 20-30% surplus cushion
  redAlertZone: number;   // 10-20% surplus cushion
  criticalZone: number;   // ≤10% surplus cushion
}

export interface MarginConfiguration {
  assetTypeAddOns: AssetTypeAddOns;
  marketCapDefinitions: MarketCapDefinitions;
  volatilityThresholds: VolatilityThresholds;
  concentrationAddOns: ConcentrationAddOns;
  warningThresholds: WarningThresholds;
  lastUpdated: Date;
  updatedBy: string;
}

// Default configuration values from LC Margin Framework v3.1
export const DEFAULT_MARGIN_CONFIG: MarginConfiguration = {
  assetTypeAddOns: {
    largeCap: 5,        // +5% add-on for large cap stocks
    midCap: 10,         // +10% add-on for mid cap stocks
    smallCap: 15,       // +15% add-on for small cap stocks
    volatile: 20,       // +20% add-on for volatile stocks (>40% volatility)
    pennyStocks: 100,   // 100% cash requirement (no margin)
    etfs: 5,           // +5% add-on for all ETFs
    bonds: 5,          // +5% add-on for all bonds
    mutualFunds: 5,    // +5% add-on for mutual funds
    options: 5,        // +5% add-on for options strategies
    other: 5           // +5% add-on for other marginable securities
  },
  marketCapDefinitions: {
    largeCap: 10000000000,  // $10B
    midCap: 2000000000,     // $2B
    smallCap: 2000000000    // Below $2B is small cap
  },
  volatilityThresholds: {
    volatileStock: 40,  // 40% (30-day annualized volatility)
    pennyStock: 5       // $5 price threshold
  },
  concentrationAddOns: {
    singlePosition30Percent: 10, // +10% for single position >30%
    singlePosition50Percent: 20, // +20% for single position >50%
    top3Positions70Percent: 10   // +10% for top 3 positions >70% (applied to concentrated positions >20%)
  },
  warningThresholds: {
    greenZone: 30,        // >30% surplus cushion
    amberAlertZone: 20,   // 20-30% surplus cushion
    redAlertZone: 10,     // 10-20% surplus cushion
    criticalZone: 10      // ≤10% surplus cushion
  },
  lastUpdated: new Date(),
  updatedBy: 'system'
};

export interface AdminUser {
  id: string;
  name: string;
  role: 'RELATIONSHIP_MANAGER' | 'OPERATIONS' | 'SENIOR_MANAGEMENT';
  canAccessAdmin: boolean;
}

// ============================================================================
// ARM Assignment Types
// ============================================================================

export interface UserInfo {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name: string;
}

export interface UserOption {
  id: number;
  email: string;
  label: string;
}

export interface ARMAssignment {
  id: number;
  arm_user_id: number;
  rm_user_id: number;
  arm_user: UserInfo;
  rm_user: UserInfo;
  assigned_by?: UserInfo;
  assigned_date: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ARMAssignmentListResponse {
  assignments: ARMAssignment[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ARMAssignmentCreate {
  arm_user_id: number;
  rm_user_id: number;
  notes?: string;
}

// ============================================================================
// IC Override Types
// ============================================================================

export interface PortfolioInfo {
  portfolio_id_lig: string;
  client_name?: string;
  official_ic_id?: string;
  official_ic_name?: string;
}

export interface PortfolioOption {
  portfolio_id_lig: string;
  label: string; // "Portfolio ID - Client Name"
}

export interface ICOverride {
  id: number;
  portfolio_id_lig: string;
  portfolio?: PortfolioInfo;
  override_ic_user_id: number;
  override_ic_user: UserInfo;
  reason: string;
  effective_date: string; // ISO date string
  created_by_id?: number;
  created_by?: UserInfo;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface ICOverrideCreate {
  portfolio_id_lig: string;
  override_ic_user_id: number;
  reason: string;
  effective_date: string; // ISO date string (YYYY-MM-DD)
}

export interface ICOverrideUpdate {
  override_ic_user_id?: number;
  reason?: string;
  effective_date?: string;
}

export interface ICOverrideListResponse {
  overrides: ICOverride[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PortfolioEffectiveIC {
  portfolio_id_lig: string;
  client_name?: string;
  official_ic_id?: string;
  official_ic_name?: string;
  override_ic_user_id?: number;
  override_ic_name?: string;
  override_ic_email?: string;
  effective_ic_id?: string;
  effective_ic_name?: string;
  override_reason?: string;
  override_effective_date?: string;
}
